import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Upload, CheckCircle2, AlertTriangle, FileText, Download } from 'lucide-react';
import { clientApi, DOC_STATUS_LABELS, DOC_STATUS_STYLES, formatDate, backendUrl, getClientToken } from '../../components/client-portal/clientPortalApi';

export default function ClientDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const reload = async () => {
    setDocs(await clientApi('/client/documents'));
  };

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleFile = async (doc, file) => {
    if (!file) return;
    setUploadingId(doc.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await clientApi(`/client/documents/${doc.id}/upload`, { method: 'POST', formData: fd });
      await reload();
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const download = async (doc) => {
    const token = getClientToken();
    const res = await fetch(`${backendUrl}/api/client/documents/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename || 'document';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const groups = docs.reduce((acc, d) => {
    const k = d.category || 'other';
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="client-documents">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Documents</h1>
        <p className="text-slate-500 mt-1 text-sm">Upload the documents your broker has requested. We'll review each one and let you know if anything needs replacing.</p>
      </div>

      {docs.length === 0 && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-8 text-center text-slate-500">
          <FileText className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3">No documents requested yet — your broker will add them shortly.</div>
        </div>
      )}

      {Object.entries(groups).map(([category, items]) => (
        <section key={category} className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
          <header className="px-5 py-3 bg-slate-50 border-b border-slate-100">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              {category.replace(/_/g, ' ')}
            </div>
          </header>
          <ul className="divide-y divide-slate-100">
            {items.map((d) => {
              const style = DOC_STATUS_STYLES[d.status] || DOC_STATUS_STYLES.requested;
              const isUploading = uploadingId === d.id;
              const canUpload = ['requested', 'rejected', 'needs_replacement'].includes(d.status);
              return (
                <li key={d.id} className="p-5 flex items-start gap-4 flex-wrap" data-testid={`doc-row-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900">{d.label}</div>
                    {d.notes && <div className="text-slate-500 text-sm mt-0.5">{d.notes}</div>}
                    {d.original_filename && (
                      <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> {d.original_filename}
                        {d.uploaded_at && <span className="text-slate-400">· uploaded {formatDate(d.uploaded_at)}</span>}
                      </div>
                    )}
                    {(d.status === 'rejected' || d.status === 'needs_replacement') && d.rejection_reason && (
                      <div className="mt-2 inline-flex items-start gap-1.5 text-rose-700 text-xs bg-rose-50 ring-1 ring-rose-200 rounded-md px-2 py-1">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                        <span>{d.rejection_reason}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full ring-1 text-[11px] font-medium ${style}`}>
                      {DOC_STATUS_LABELS[d.status] || d.status}
                    </span>
                    {canUpload && (
                      <>
                        <input
                          ref={(el) => (fileRefs.current[d.id] = el)}
                          type="file"
                          className="hidden"
                          data-testid={`doc-upload-input-${d.id}`}
                          onChange={(e) => handleFile(d, e.target.files?.[0])}
                        />
                        <button
                          type="button"
                          disabled={isUploading}
                          data-testid={`doc-upload-btn-${d.id}`}
                          onClick={() => fileRefs.current[d.id]?.click()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                        >
                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          {d.original_filename ? 'Replace' : 'Upload'}
                        </button>
                      </>
                    )}
                    {d.status === 'approved' && (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                        <CheckCircle2 className="w-4 h-4" /> Approved
                      </span>
                    )}
                    {d.original_filename && (
                      <button
                        type="button"
                        onClick={() => download(d)}
                        className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-xs"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
